
import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function SellingTimelinePage() {
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState('standard');

  const toggleStep = (stepNumber: number) => {
    setCompletedSteps(prev =>
      prev.includes(stepNumber)
        ? prev.filter(num => num !== stepNumber)
        : [...prev, stepNumber]
    );
  };

  const timeframes = {
    fast: { label: 'Fast Sale (30-45 days)', multiplier: 0.7 },
    standard: { label: 'Standard Sale (60-90 days)', multiplier: 1 },
    luxury: { label: 'Luxury/Unique (90-120 days)', multiplier: 1.4 }
  };

  const sellingSteps = [
    {
      phase: 'Preparation Phase',
      duration: { fast: '1-2 weeks', standard: '2-3 weeks', luxury: '3-4 weeks' },
      color: 'bg-black',
      steps: [
        {
          number: 1,
          title: 'Initial Consultation & Market Analysis',
          description:
            'Meet with your realtor to discuss goals, timeline, and get a comprehensive market analysis.',
          duration: '1-2 days',
          tasks: [
            'Review recent comparable sales',
            'Assess current market conditions',
            'Set realistic price expectations',
            'Discuss marketing strategy'
          ]
        },
        {
          number: 2,
          title: 'Home Preparation & Staging',
          description:
            'Prepare your home to make the best impression on potential buyers.',
          duration: '1-2 weeks',
          tasks: [
            'Complete necessary repairs',
            'Deep clean entire home',
            'Stage rooms for maximum appeal',
            'Enhance curb appeal'
          ]
        },
        {
          number: 3,
          title: 'Professional Photography & Marketing Materials',
          description:
            'Create compelling marketing materials to showcase your home.',
          duration: '2-3 days',
          tasks: [
            'Professional photography session',
            'Create virtual tour if needed',
            'Write compelling listing description',
            'Prepare marketing flyers'
          ]
        }
      ]
    },
    {
      phase: 'Marketing Phase',
      duration: { fast: '1-2 weeks', standard: '2-4 weeks', luxury: '4-6 weeks' },
      color: 'bg-yellow-500',
      steps: [
        {
          number: 4,
          title: 'List Your Home',
          description:
            'Your home goes live on the market with maximum exposure.',
          duration: '1 day',
          tasks: [
            'Upload to MLS system',
            'Publish on major real estate websites',
            'Share on social media platforms',
            'Notify agent networks'
          ]
        },
        {
          number: 5,
          title: 'Showings & Open Houses',
          description: 'Welcome potential buyers to view your home.',
          duration: 'Ongoing',
          tasks: [
            'Schedule private showings',
            'Host weekend open houses',
            'Collect buyer feedback',
            'Adjust strategy if needed'
          ]
        },
        {
          number: 6,
          title: 'Review & Negotiate Offers',
          description: 'Evaluate offers and negotiate the best terms.',
          duration: '1-3 days per offer',
          tasks: [
            'Review offer details carefully',
            'Negotiate price and terms',
            'Consider contingencies',
            'Accept the best offer'
          ]
        }
      ]
    },
    {
      phase: 'Closing Phase',
      duration: { fast: '2-3 weeks', standard: '3-4 weeks', luxury: '4-5 weeks' },
      color: 'bg-yellow-500',
      steps: [
        {
          number: 7,
          title: 'Under Contract',
          description:
            'Buyer begins due diligence process with inspections and financing.',
          duration: '1-2 weeks',
          tasks: [
            'Buyer conducts home inspection',
            'Buyer secures financing',
            'Address any inspection issues',
            'Buyer orders appraisal'
          ]
        },
        {
          number: 8,
          title: 'Final Preparations',
          description: 'Complete final requirements before closing.',
          duration: '1 week',
          tasks: [
            'Complete any agreed repairs',
            'Prepare for final walkthrough',
            'Coordinate moving plans',
            'Gather closing documents'
          ]
        },
        {
          number: 9,
          title: 'Closing Day',
          description: 'Transfer ownership and receive your proceeds.',
          duration: '1-2 hours',
          tasks: [
            'Final walkthrough with buyer',
            'Sign closing documents',
            'Transfer keys and garage remotes',
            'Receive your proceeds'
          ]
        }
      ]
    }
  ];

  const totalSteps = sellingSteps.reduce(
    (sum, phase) => sum + phase.steps.length,
    0
  );
  const progressPercentage = (completedSteps.length / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-black mb-4">
              Home Selling Timeline &amp; Process
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your step-by-step guide to selling your home successfully. Track
              your progress and know what to expect at each stage of the
              process.
            </p>
          </div>

          {/* Timeline Selector */}
          <div className="bg-black text-white rounded-lg p-6 mb-8">
            <h3 className="text-xl font-bold mb-4 text-center">
              Choose Your Selling Timeline
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {Object.entries(timeframes).map(([key, timeframe]) => (
                <button
                  key={key}
                  onClick={() => setSelectedTimeframe(key)}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    selectedTimeframe === key
                      ? 'bg-yellow-500 text-black'
                      : 'bg-white text-black hover:bg-gray-100'
                  }`}
                >
                  {timeframe.label}
                </button>
              ))}
            </div>
          </div>

          {/* Progress Tracker */}
          <div className="bg-white border-2 border-yellow-500 rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-black">
                Your Selling Progress
              </h3>
              <div className="text-yellow-600 font-bold">
                {completedSteps.length} / {totalSteps} Steps Complete
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-yellow-500 h-4 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <p className="text-gray-600 text-sm mt-2">
              Complete each step to stay on track for a successful sale.
            </p>
          </div>

          {/* Timeline Phases */}
          <div className="space-y-8">
            {sellingSteps.map((phase, phaseIndex) => (
              <div
                key={phaseIndex}
                className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden"
              >
                <div className={`${phase.color} text-white p-6`}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">{phase.phase}</h2>
                    <div className="text-right">
                      <div className="text-lg font-semibold">
                        {
                          phase.duration[
                            selectedTimeframe as keyof typeof phase.duration
                          ]
                        }
                      </div>
                      <div className="text-sm opacity-90">
                        Expected Duration
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-6">
                    {phase.steps.map(step => (
                      <div
                        key={step.number}
                        className="flex items-start space-x-4"
                      >
                        <button
                          onClick={() => toggleStep(step.number)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm cursor-pointer transition-colors ${
                            completedSteps.includes(step.number)
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {completedSteps.includes(step.number) ? (
                            <i className="ri-check-line"></i>
                          ) : (
                            step.number
                          )}
                        </button>

                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h3
                              className={`text-lg font-bold transition-colors ${
                                completedSteps.includes(step.number)
                                  ? 'text-green-600 line-through'
                                  : 'text-black'
                              }`}
                            >
                              {step.title}
                            </h3>
                            <span className="text-sm text-gray-500 font-medium">
                              {step.duration}
                            </span>
                          </div>

                          <p
                            className={`text-gray-600 mb-3 ${
                              completedSteps.includes(step.number)
                                ? 'opacity-60'
                                : ''
                            }`}
                          >
                            {step.description}
                          </p>

                          <div className="grid md:grid-cols-2 gap-2">
                            {step.tasks.map((task, taskIndex) => (
                              <div
                                key={taskIndex}
                                className={`flex items-center space-x-2 text-sm ${
                                  completedSteps.includes(step.number)
                                    ? 'opacity-60'
                                    : ''
                                }`}
                              >
                                <i className="ri-checkbox-circle-line text-yellow-500"></i>
                                <span className="text-gray-700">{task}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Key Factors Section */}
          <div className="mt-12 bg-gray-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-black mb-6 text-center">
              Factors That Affect Your Timeline
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-price-tag-3-line text-2xl text-black"></i>
                </div>
                <h4 className="font-bold text-black mb-2">
                  Pricing Strategy
                </h4>
                <p className="text-gray-600 text-sm">
                  Competitive pricing attracts more buyers and faster offers.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-calendar-line text-2xl text-black"></i>
                </div>
                <h4 className="font-bold text-black mb-2">
                  Market Conditions
                </h4>
                <p className="text-gray-600 text-sm">
                  Seller&apos;s vs buyer&apos;s market affects timeline
                  significantly.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-home-heart-line text-2xl text-black"></i>
                </div>
                <h4 className="font-bold text-black mb-2">Home Condition</h4>
                <p className="text-gray-600 text-sm">
                  Well-maintained, staged homes sell faster than fixer-uppers.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-map-pin-line text-2xl text-black"></i>
                </div>
                <h4 className="font-bold text-black mb-2">Location</h4>
                <p className="text-gray-600 text-sm">
                  Desirable neighborhoods typically see faster sales.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <div className="bg-black text-white rounded-lg p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Start Your Selling Journey?
              </h3>
              <p className="text-gray-300 mb-6">
                Let&apos;s create a customized timeline and strategy for your
                specific situation. Every home and seller is unique, and your
                plan should be too.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() =>
                    document
                      .getElementById('contact')
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="bg-yellow-500 text-black px-8 py-3 rounded-lg hover:bg-yellow-400 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Listing Consultation
                </button>
                <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-black transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
